import { useEffect, useState } from 'react';

import type { CadenceResponse } from '@smartapply/shared';

import { PageShell } from '../components/layout/PageShell.js';
import { Stamp } from '../components/ui/Stamp.js';
import { SmartApplyApiError } from '../lib/api-client.js';
import { fetchCadence, setWeeklyGoal } from '../lib/api-client.js';

export function CadenceRoute() {
  const [data, setData] = useState<CadenceResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function refresh() {
    try {
      const c = await fetchCadence();
      setData(c);
    } catch (err) {
      setError(err instanceof SmartApplyApiError ? err.message : 'Falha ao carregar.');
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  if (error) {
    return (
      <PageShell>
        <p className="font-sans text-sm text-[var(--accent-crimson)]">{error}</p>
      </PageShell>
    );
  }
  if (!data) {
    return (
      <PageShell>
        <p className="font-mono text-xs uppercase tracking-[0.18em] text-[var(--ink-tertiary)]">
          carregando...
        </p>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <header className="mb-10">
        <p className="mb-4 inline-block">
          <Stamp>Cadência</Stamp>
        </p>
        <h1 className="font-serif text-[36px] font-medium leading-[1.1] tracking-[-0.02em] text-[var(--ink-primary)] sm:text-[40px]">
          Sua presença, em ritmo
          <span className="text-[var(--accent-saffron)]">.</span>
        </h1>
        <p className="mt-3 max-w-2xl font-sans text-sm text-[var(--ink-secondary)]">
          Histórico autodeclarado: a app não acessa seu LinkedIn. Marque "publicado"
          quando o draft virar post de verdade — assim a contagem fica honesta.
        </p>
      </header>

      <Markers data={data} />
      <WeeklyGoalCard data={data} onChange={() => void refresh()} />
      <SeverityTimeline data={data} />
      <RecentDraftsList data={data} />
    </PageShell>
  );
}

function Markers({ data }: { data: CadenceResponse }) {
  return (
    <section className="mb-10 grid gap-4 sm:grid-cols-3">
      <Marker
        label="análises (30 dias)"
        big={data.analysesLast30Days}
        sub={data.lastAnalysisAt ? agoLabel(data.lastAnalysisAt) : 'sem análise ainda'}
      />
      <Marker
        label="drafts gerados (7 dias)"
        big={data.postsGeneratedLast7Days}
        sub={
          data.lastPostGeneratedAt
            ? `último ${agoLabel(data.lastPostGeneratedAt)}`
            : 'sem drafts ainda'
        }
      />
      <Marker
        label="drafts gerados (30 dias)"
        big={data.postsGeneratedLast30Days}
        sub="janela mensal"
      />
    </section>
  );
}

function Marker({ label, big, sub }: { label: string; big: number; sub: string }) {
  return (
    <article className="card-paper rounded-sm px-5 py-4">
      <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--ink-tertiary)]">
        {label}
      </p>
      <p className="mt-2 font-serif text-4xl tabular-nums text-[var(--ink-primary)]">
        {big}
      </p>
      <p className="mt-1 font-sans text-xs italic text-[var(--ink-tertiary)]">{sub}</p>
    </article>
  );
}

function WeeklyGoalCard({
  data,
  onChange,
}: {
  data: CadenceResponse;
  onChange: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(String(data.weeklyGoal ?? 2));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const goal = data.weeklyGoal ?? 0;
  const done = data.postsPublishedThisWeek;
  const pct = goal > 0 ? Math.min(100, Math.round((done / goal) * 100)) : 0;

  async function save() {
    setBusy(true);
    setErr(null);
    try {
      const value = draft.trim() === '' ? null : Number(draft);
      if (value !== null && (Number.isNaN(value) || value < 0 || value > 20)) {
        setErr('Use um número entre 0 e 20.');
        return;
      }
      await setWeeklyGoal({ weeklyGoal: value });
      setEditing(false);
      onChange();
    } catch (e) {
      setErr(e instanceof SmartApplyApiError ? e.message : 'Falha ao salvar.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <article
      className="card-paper mb-10 rounded-sm px-7 py-6"
      style={{ borderLeft: '3px solid var(--accent-forest)' }}
    >
      <div className="flex flex-wrap items-baseline justify-between gap-4">
        <div>
          <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-[var(--accent-forest)]">
            meta semanal
          </p>
          <p className="mt-2 font-serif text-2xl text-[var(--ink-primary)]">
            {goal > 0 ? (
              <>
                <span className="tabular-nums">{done}</span> de{' '}
                <span className="tabular-nums">{goal}</span> posts publicados esta
                semana
              </>
            ) : (
              'Defina sua meta semanal'
            )}
          </p>
          <p className="mt-1 font-sans text-xs italic text-[var(--ink-tertiary)]">
            Mês: {data.postsPublishedThisMonth} publicados ·{' '}
            {data.lastPostPublishedAt
              ? `último ${agoLabel(data.lastPostPublishedAt)}`
              : 'nenhum marcado ainda'}
          </p>
        </div>
        {!editing && (
          <button
            type="button"
            onClick={() => {
              setDraft(String(data.weeklyGoal ?? 2));
              setEditing(true);
            }}
            className="font-mono text-[11px] uppercase tracking-[0.18em] text-[var(--accent-saffron)] hover:underline"
          >
            {goal > 0 ? 'editar meta' : 'definir meta'}
          </button>
        )}
      </div>

      {goal > 0 && !editing && (
        <div className="mt-5">
          <div className="h-2 rounded-sm bg-[var(--bg-paper-recessed)]">
            <div
              className="h-2 rounded-sm transition-all"
              style={{
                width: `${pct}%`,
                backgroundColor:
                  pct >= 100 ? 'var(--accent-forest)' : 'var(--accent-saffron)',
              }}
            />
          </div>
          <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--ink-tertiary)]">
            {pct}% da meta
          </p>
        </div>
      )}

      {editing && (
        <div className="mt-5 flex flex-wrap items-end gap-3">
          <div>
            <label className="block font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--ink-tertiary)]">
              posts/semana
            </label>
            <input
              type="number"
              min={0}
              max={20}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              className="mt-2 w-24 rounded-sm border border-[var(--border-default)] bg-[var(--bg-paper)] px-3 py-2 font-mono text-base tabular-nums text-[var(--ink-primary)] focus:border-[var(--accent-saffron)] focus:outline-none"
            />
          </div>
          <button
            type="button"
            onClick={() => void save()}
            disabled={busy}
            className="rounded-sm bg-[var(--ink-primary)] px-4 py-2 font-sans text-sm text-[var(--bg-paper)] hover:opacity-90 disabled:opacity-40"
          >
            {busy ? '...' : 'salvar'}
          </button>
          <button
            type="button"
            onClick={() => setEditing(false)}
            className="font-mono text-[11px] uppercase tracking-[0.18em] text-[var(--ink-tertiary)] hover:text-[var(--ink-primary)]"
          >
            cancelar
          </button>
          {err && <p className="basis-full font-sans text-xs text-[var(--accent-crimson)]">{err}</p>}
        </div>
      )}
    </article>
  );
}

function SeverityTimeline({ data }: { data: CadenceResponse }) {
  if (data.analysisTimeline.length === 0) return null;

  const maxFindings = Math.max(...data.analysisTimeline.map((p) => p.findingsCount), 1);

  return (
    <section className="mb-12">
      <h2 className="mb-2 font-serif text-2xl font-medium text-[var(--ink-primary)]">
        Análises ao longo do tempo
      </h2>
      <p className="mb-6 font-sans text-sm italic text-[var(--ink-tertiary)]">
        Barra cheia: total de findings. Pedaço vermelho: críticos.
      </p>

      <ol className="space-y-3">
        {data.analysisTimeline.map((p) => {
          const totalPct = (p.findingsCount / maxFindings) * 100;
          const critPct = (p.criticalCount / maxFindings) * 100;
          return (
            <li key={p.id} className="grid gap-2 sm:grid-cols-[140px_1fr_auto] sm:items-center">
              <span className="font-mono text-[10px] tabular-nums text-[var(--ink-tertiary)]">
                {new Date(p.createdAt).toLocaleDateString('pt-BR')}
              </span>
              <div className="relative h-3 rounded-sm bg-[var(--bg-paper-recessed)]">
                <div
                  className="absolute inset-y-0 left-0 rounded-sm bg-[var(--ink-tertiary)]"
                  style={{ width: `${totalPct}%` }}
                />
                <div
                  className="absolute inset-y-0 left-0 rounded-sm bg-[var(--accent-crimson)]"
                  style={{ width: `${critPct}%` }}
                />
              </div>
              <span className="font-mono text-xs tabular-nums text-[var(--ink-secondary)]">
                {p.findingsCount} ({p.criticalCount} crit)
              </span>
            </li>
          );
        })}
      </ol>
    </section>
  );
}

function RecentDraftsList({ data }: { data: CadenceResponse }) {
  if (data.recentDrafts.length === 0) return null;

  return (
    <section>
      <h2 className="mb-2 font-serif text-2xl font-medium text-[var(--ink-primary)]">
        Drafts recentes
      </h2>
      <p className="mb-6 font-sans text-sm italic text-[var(--ink-tertiary)]">
        Últimos 10 sets de drafts gerados. Verde = você marcou como publicado.
      </p>
      <ul className="space-y-3">
        {data.recentDrafts.map((d) => (
          <li
            key={d.id}
            className="card-paper rounded-sm px-5 py-3"
            style={{
              borderLeft: d.publishedAt
                ? '3px solid var(--accent-forest)'
                : '3px solid var(--ink-tertiary)',
            }}
          >
            <div className="flex flex-wrap items-baseline justify-between gap-3">
              <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--ink-tertiary)]">
                {d.sourceType} · {d.draftsCount} drafts
                {d.publishedAt && (
                  <span className="ml-2 text-[var(--accent-forest)]">
                    · publicado {agoLabel(d.publishedAt)}
                  </span>
                )}
              </p>
              <span className="font-mono text-[10px] tabular-nums text-[var(--ink-muted)]">
                {new Date(d.createdAt).toLocaleString('pt-BR')}
              </span>
            </div>
            <p className="mt-1 font-serif text-sm text-[var(--ink-primary)]">
              {d.label || truncate(d.sourceInput, 80)}
            </p>
          </li>
        ))}
      </ul>
    </section>
  );
}

function agoLabel(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const min = Math.floor(ms / 60_000);
  if (min < 1) return 'agora há pouco';
  if (min < 60) return `há ${min} min`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `há ${hr}h`;
  const d = Math.floor(hr / 24);
  if (d < 7) return `há ${d}d`;
  const w = Math.floor(d / 7);
  if (w < 5) return `há ${w} sem`;
  const mo = Math.floor(d / 30);
  return `há ${mo} mês${mo > 1 ? 'es' : ''}`;
}

function truncate(s: string, n: number): string {
  if (s.length <= n) return s;
  return s.slice(0, n - 1) + '…';
}
